import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Crown } from 'lucide-react';

/**
 * Shared chrome for static legal pages (Privacy Policy, Terms of Service).
 *
 * Renders a consistent dark-themed layout with:
 *   • Sticky header with back-button (history.back when there's a referrer,
 *     else home), brand mark, and a small "Legal" eyebrow.
 *   • Centred reading column (max-w-3xl) sized for long-form content.
 *   • Footer cross-linking the two policies + back to home, so a player
 *     who lands on Privacy can jump straight to Terms in one tap.
 */
export default function LegalLayout({ title, effectiveDate, children }) {
  const navigate = useNavigate();

  const goBack = () => {
    // History API: if user navigated here from inside the app, take them
    // back. Otherwise route to / so direct-link visitors don't end up
    // outside the app on browser-back.
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] text-gray-200" data-testid="legal-page">
      {/* Header */}
      <header
        className="sticky top-0 z-30 backdrop-blur-md"
        style={{
          background: 'rgba(11,14,20,0.85)',
          borderBottom: '1px solid rgba(212,175,55,0.18)',
        }}
      >
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={goBack}
            className="flex items-center gap-2 text-gray-300 hover:text-white text-sm transition"
            data-testid="legal-back-btn"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <Link to="/" className="flex items-center gap-2" data-testid="legal-home-link">
            <Crown className="w-5 h-5 text-[#D4AF37]" />
            <span className="font-bold tracking-wide" style={{ color: '#D4AF37' }}>Vegas Lobby</span>
          </Link>
          <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 hidden sm:block">Legal</div>
          <div className="sm:hidden w-12" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]/80 mb-3" data-testid="legal-eyebrow">
          Vegas Lobby · Legal
        </p>
        <h1 className="text-3xl sm:text-4xl font-black text-white leading-tight" data-testid="legal-title">
          {title}
        </h1>
        {effectiveDate && (
          <p className="text-sm text-gray-500 mt-3" data-testid="legal-effective-date">
            Effective Date: <span className="text-gray-300">{effectiveDate}</span>
          </p>
        )}

        <div
          className="mt-10 space-y-6 text-[15px] leading-relaxed text-gray-300"
          data-testid="legal-body"
        >
          {children}
        </div>

        {/* Cross-link footer */}
        <div
          className="mt-16 pt-8 grid grid-cols-1 sm:grid-cols-3 gap-3"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
        >
          <Link
            to="/privacy"
            className="rounded-xl px-4 py-3 text-sm font-semibold text-center transition hover:scale-[1.02]"
            style={{
              background: 'rgba(212,175,55,0.06)',
              border: '1px solid rgba(212,175,55,0.18)',
              color: '#F5D76E',
            }}
            data-testid="legal-link-privacy"
          >
            Privacy Policy
          </Link>
          <Link
            to="/terms"
            className="rounded-xl px-4 py-3 text-sm font-semibold text-center transition hover:scale-[1.02]"
            style={{
              background: 'rgba(212,175,55,0.06)',
              border: '1px solid rgba(212,175,55,0.18)',
              color: '#F5D76E',
            }}
            data-testid="legal-link-terms"
          >
            Terms of Service
          </Link>
          <Link
            to="/"
            className="rounded-xl px-4 py-3 text-sm font-semibold text-center transition hover:scale-[1.02]"
            style={{
              background: 'linear-gradient(135deg, #D4AF37, #F5D76E)',
              color: '#000',
            }}
            data-testid="legal-link-home"
          >
            Back to Vegas Lobby
          </Link>
        </div>

        <p className="text-[11px] text-gray-600 text-center mt-8" data-testid="legal-copyright">
          2026 Vegas Lobby. All rights reserved.
        </p>
      </main>
    </div>
  );
}

/* Helpers — small, focused presentational pieces so the policy pages stay
   readable and don't repeat tailwind classes everywhere. */

export function LegalSection({ number, title, children }) {
  return (
    <section
      className="space-y-3"
      data-testid={`legal-section-${number}`}
    >
      <h2 className="text-lg sm:text-xl font-bold text-white flex items-baseline gap-2">
        <span className="text-[#D4AF37] tabular-nums">{number}.</span>
        <span>{title}</span>
      </h2>
      <div className="space-y-3 pl-0 sm:pl-6">{children}</div>
    </section>
  );
}

export function LegalSubhead({ children }) {
  return (
    <h3 className="text-[15px] font-semibold text-gray-100 mt-2">{children}</h3>
  );
}

export function LegalList({ items, variant = 'check' }) {
  return (
    <ul className="space-y-1.5" data-testid="legal-list">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2.5 text-[14.5px] text-gray-300">
          <span
            className="shrink-0 mt-2 w-1.5 h-1.5 rounded-full"
            style={{ background: variant === 'cross' ? 'rgba(239,68,68,0.7)' : '#D4AF37' }}
          />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function LegalParagraph({ children }) {
  return <p className="text-[15px] text-gray-300 leading-relaxed">{children}</p>;
}

export function LegalCallout({ children }) {
  return (
    <div
      className="rounded-xl px-4 py-3 text-[14px]"
      style={{
        background: 'rgba(212,175,55,0.07)',
        border: '1px solid rgba(212,175,55,0.22)',
        color: '#F5D76E',
      }}
      data-testid="legal-callout"
    >
      {children}
    </div>
  );
}
